import React from 'react';
import PropTypes from 'prop-types';
import { useThemeTokensCallback } from '../ThemeProvider';
import { selectTokens, getTokensPropType, linkProps } from '../utils';
import LinkBase from './LinkBase';

/**
 * `ChevronLink` is a convenience wrapper around the `Link` component to enable "directional" links.
 * It effectively pre-binds left and right icons, and a directional translation of the icon on hover.
 *
 * ChevronLink is not intended to be deeply themable; variants passed to ChevronLink are forwarded to Link.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const ChevronLink = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    direction = 'right',
    children,
    tokens = {},
    variant,
    dataSet,
    ...otherlinkProps
  } = _ref;
  const getChevronTokens = useThemeTokensCallback('ChevronLink', tokens, variant);
  const applyChevronTokens = linkState => {
    const {
      leftIcon,
      rightIcon,
      iconDisplace,
      height,
      fontSize,
      ...otherTokens
    } = getChevronTokens(linkState);
    const linkTokens = selectTokens('Link', otherTokens);
    return {
      ...linkTokens,
      height,
      blockFontSize: tokens.fontSize ?? fontSize,
      icon: direction === 'right' ? rightIcon : leftIcon,
      iconTranslateX: iconDisplace * (direction === 'right' ? 1 : -1) || 0
    };
  };
  const getTokens = useThemeTokensCallback('Link', applyChevronTokens, variant);
  return /*#__PURE__*/_jsx(LinkBase, {
    ...otherlinkProps,
    iconPosition: direction,
    tokens: getTokens,
    dataSet: dataSet,
    ref: ref,
    children: children
  });
});
ChevronLink.displayName = 'ChevronLink';
ChevronLink.propTypes = {
  ...LinkBase.propTypes,
  children: PropTypes.node,
  variant: PropTypes.exact({
    size: PropTypes.oneOf(['large', 'small', 'micro']),
    alternative: PropTypes.bool,
    inverse: PropTypes.bool
  }),
  ...linkProps.types,
  tokens: getTokensPropType('ChevronLink', 'Link'),
  /**
   * Changes direction of chevron icon
   */
  direction: PropTypes.oneOf(['left', 'right'])
};
export default ChevronLink;